import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  useBillingControllerAdmissionMutation,
  useBillingControllerEnrollMutation,
  useBillingControllerManagerQuery,
  useBillingControllerOptionsQuery,
} from '@shared/services/apiService';

import { billingErrorMessage } from '../model/billingError';
import { BillingSummary } from './BillingSummary';
import { BillingPaymentExceptions } from './BillingPaymentExceptions';

/** Existing platform Manager boundary; enrollment is an explicit audited cohort action. */
export const BillingOperatorPanel = ({ companyId }: { companyId: string }) => {
  const summary = useBillingControllerManagerQuery({ companyId });
  const options = useBillingControllerOptionsQuery();
  const [enroll, enrollState] = useBillingControllerEnrollMutation();
  const [admission, admissionState] = useBillingControllerAdmissionMutation();
  const [policyId, setPolicyId] = useState('');
  const [reason, setReason] = useState('');
  const [action, setAction] = useState<'enroll' | 'enable' | 'pause' | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const keys = useRef(new Map<string, string>());
  const busy = enrollState.isLoading || admissionState.isLoading;
  const policy = options.data?.policies.find((p) => p.id === policyId);
  const confirm = async () => {
    if (!action || !reason.trim()) return;
    const semantic = JSON.stringify([
      companyId,
      action,
      action === 'enroll' ? policyId : null,
      reason.trim(),
    ]);
    if (!keys.current.has(semantic)) keys.current.set(semantic, crypto.randomUUID());
    const requestKey = keys.current.get(semantic)!;
    setError('');
    try {
      if (action === 'enroll')
        await enroll({
          companyId,
          billingEnrollmentDto: { policyId, reason: reason.trim(), requestKey },
        }).unwrap();
      else
        await admission({
          companyId,
          billingAdmissionControlDto: {
            enabled: action === 'enable',
            reason: reason.trim(),
            requestKey,
          },
        }).unwrap();
      keys.current.delete(semantic);
      setAction(null);
      setMessage(
        'The server recorded the operator action. The summary below shows current access.',
      );
      await summary.refetch();
    } catch (value) {
      setError(billingErrorMessage(value));
      void summary.refetch();
    }
  };
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Company billing controls</Typography>
      <Typography color="text.secondary">
        Enrollment applies only to eligible unused companies in an approved cohort. New-call
        admission can be paused independently; receipt, settlement and reconciliation continue, and
        active calls may finish.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="info">{message}</Alert>}
      {(summary.isError || options.isError) && (
        <Alert
          severity="error"
          action={
            <Button
              onClick={() => {
                void summary.refetch();
                void options.refetch();
              }}>
              Retry
            </Button>
          }>
          Operator billing information could not be loaded.
        </Alert>
      )}
      {summary.data && <BillingSummary summary={summary.data} />}
      {summary.data?.mode === 'credit_managed' && (
        <BillingPaymentExceptions key={companyId} companyId={companyId} />
      )}
      {summary.data?.mode === 'legacy_manual' && (
        <>
          {!options.data?.enrollmentEnabled && (
            <Alert severity="info">
              New-company enrollment is paused by deployment configuration.
            </Alert>
          )}
          <TextField
            select
            label="Published enrollment policy"
            value={policyId}
            onChange={(e) => setPolicyId(e.target.value)}
            disabled={busy}>
            {options.data?.policies.map((p) => (
              <MenuItem key={p.id} value={p.id} disabled={p.policyGates.length > 0}>
                {p.key} · {p.environment} · {p.trialPlanKey}
              </MenuItem>
            ))}
          </TextField>
          {policy && (
            <Typography color="text.secondary">
              {policy.environment === 'test'
                ? 'Test environment; this does not approve commercial policy. '
                : ''}
              Trial duration: {policy.trialDurationSeconds} seconds. Cohort begins:{' '}
              {new Date(policy.cohortStartsAt).toLocaleString()}.
            </Typography>
          )}
          {options.data?.hasMore && (
            <Alert severity="info">
              Only the 100 most recent policies are listed. Ask an operator to review the published
              policy inventory if the required policy is missing.
            </Alert>
          )}
        </>
      )}
      <TextField
        label="Audit reason"
        multiline
        minRows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={busy}
        inputProps={{ maxLength: 2000 }}
      />
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
        <Button onClick={() => void summary.refetch()} disabled={summary.isFetching}>
          Refresh billing status
        </Button>
        {summary.data?.mode === 'legacy_manual' && (
          <Button
            variant="outlined"
            disabled={
              busy ||
              !reason.trim() ||
              !policy ||
              policy.policyGates.length > 0 ||
              !options.data?.enrollmentEnabled
            }
            onClick={() => setAction('enroll')}>
            Enroll eligible company
          </Button>
        )}
        {summary.data?.mode === 'credit_managed' && (
          <Button
            variant="outlined"
            disabled={
              busy ||
              !reason.trim() ||
              (!summary.data.admissionEnabled && !!summary.data.policyGates.length)
            }
            onClick={() => setAction(summary.data?.admissionEnabled ? 'pause' : 'enable')}>
            {summary.data.admissionEnabled ? 'Pause new calls' : 'Enable new calls'}
          </Button>
        )}
      </Stack>
      <Dialog
        open={!!action}
        onClose={() => {
          if (!busy) setAction(null);
        }}
        fullWidth
        maxWidth="sm">
        <DialogTitle>Confirm operator billing action</DialogTitle>
        <DialogContent>
          <Typography>
            {action === 'enroll'
              ? 'Enroll this eligible company under the selected published policy. This does not migrate other companies or create a paid subscription.'
              : action === 'enable'
                ? 'Enable new-call admission only if the current account, period and product-policy checks pass.'
                : 'Pause new calls while active calls and outstanding financial obligations continue to completion.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)} disabled={busy}>
            Back
          </Button>
          <Button variant="contained" onClick={() => void confirm()} disabled={busy}>
            Confirm operator action
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
